import React, { useState } from 'react';
import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { Close, ReceiptLong } from '@mui/icons-material';

import { IKiDatevExportProposal } from '../Interfaces/IKi';
import { DatevExportDialog } from '../Invoice/InvoiceDatevExportDialog';

interface IProps {
  proposal: IKiDatevExportProposal;
}

const euro = (wert: number) =>
  wert.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const datum = (iso: string) => iso.split('-').reverse().join('.');

/**
 * Vorschlagskarte für einen DATEV-Export (#1336).
 *
 * Die KI hat den Zeitraum bestimmt und gezählt, was darin liegt. Exportiert
 * wird im **Exportdialog** der Rechnungsübersicht — derselbe, mit denselben
 * gemerkten Berater- und Kontenangaben und derselben Prüfung. Die Karte
 * öffnet ihn nur vorbelegt.
 */
export const KiDatevExportProposalCard: React.FC<IProps> = ({ proposal }) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  if (isDismissed) {
    return null;
  }

  const { Payload, Display } = proposal;

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderColor: 'primary.light' }}>
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <ReceiptLong fontSize='small' color='primary' />
          <Typography variant='subtitle2'>DATEV-Export</Typography>
        </Stack>

        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
          <Chip
            size='small'
            label={`${datum(Payload.von)} – ${datum(Payload.bis)}`}
          />
          <Chip
            size='small'
            variant='outlined'
            label={`${Display.AnzahlRechnungen} Rechnungen · ${euro(
              Display.SummeRechnungen
            )}`}
          />
          {Payload.mitZahlungen && (
            <Chip
              size='small'
              variant='outlined'
              label={`${Display.AnzahlZahlungen} Zahlungen · ${euro(
                Display.SummeZahlungen
              )}`}
            />
          )}
        </Stack>

        {proposal.Hinweise.length > 0 && (
          <Alert severity='info' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {proposal.Hinweise.map(hinweis => (
                <Typography component='li' variant='caption' key={hinweis}>
                  {hinweis}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            onClick={() => setIsDismissed(true)}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={<ReceiptLong />}
            onClick={() => setIsOpen(true)}
          >
            Export öffnen
          </Button>
        </Stack>
      </Stack>

      {isOpen && (
        <DatevExportDialog
          onClose={() => setIsOpen(false)}
          vorbelegung={Payload}
        />
      )}
    </Paper>
  );
};
